import React, { memo, useCallback, useMemo } from "react";

import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { getTagMention } from "modules/utils/Functions";
import colors from "app/colors";
import { CircleMinusIcon, WarningIcon } from "modules/icons";
import Toast from "modules/toasts/toast";
import { useTags } from "modules/providers/tags-provider";
import {
  AdminTagsSetActiveMutation,
  AdminTagsSetActiveMutationVariables,
} from "gql/graphql";
import ADMIN_TAGS_SET_ACTIVE_MUTATION from "./_networking/admin-tags-set-active";
import { useOverlay } from "modules/providers/overlay-provider";
import useDisplayToast from "modules/hooks/use-display-toast";

interface IProps {
  tagIds: string[];
}

const AdminConfirmArchiveTagModal = ({ tagIds }: IProps) => {
  const { closeModal } = useOverlay();
  const { tags: data } = useTags();
  const { displayToast } = useDisplayToast();
  const [adminTagsSetActiveApi] = useMutation<
    AdminTagsSetActiveMutation,
    AdminTagsSetActiveMutationVariables
  >(ADMIN_TAGS_SET_ACTIVE_MUTATION);

  const tags = useMemo(
    () => data?.filter((tag) => tagIds.includes(tag.id)),
    [data, tagIds],
  );

  const tagsFirstThree = tags.slice(0, 3);

  const onCloseClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      closeModal();
    },
    [closeModal],
  );

  const onArchiveClicked = useCallback(() => {
    adminTagsSetActiveApi({
      variables: {
        active: false,
        idList: tagIds,
      },
    });
    displayToast(() => (
      <Toast
        message={`${tagIds.length} Tags Archived`}
        leftIcon={<CircleMinusIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
    closeModal();
  }, [adminTagsSetActiveApi, closeModal, tagIds, displayToast]);

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader pt={8}>
          <VStack spacing={6}>
            <WarningIcon />
            <Text fontSize={26}>Archive Tag{tags.length > 1 && "s"}?</Text>
          </VStack>
        </ModalHeader>
        <ModalBody pb={6}>
          <VStack spacing={6}>
            <Text fontSize={16} color={colors.gray50} textAlign="center">
              Are you sure you want to archive:
              <Text>
                {tagsFirstThree.map((tag, index) => (
                  <React.Fragment key={tag.id}>
                    {index < 3 && index > 0 && ", "}
                    {getTagMention(tag)}
                  </React.Fragment>
                ))}
                {tags.length > 3 ? `, and ${tags.length - 3} more?` : "?"}
              </Text>
            </Text>
            <Text fontSize={12} color={colors.gray50} textAlign="center">
              Archiving tags that automatically tag conversations will also be
              removed from that group.
            </Text>
            <HStack>
              <Button
                variant="cancel"
                height={8}
                w={28}
                onClick={onCloseClicked}
              >
                No
              </Button>
              <Button height={8} w={28} onClick={onArchiveClicked}>
                Archive
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminConfirmArchiveTagModal);
